import { useEffect, useRef } from "react";
import Api from "api/Api";

const api = new Api();

const CHECK_INTERVAL_MS = 30 * 1000;
const MIN_BUFFER_MS = 30 * 1000;
const MAX_BUFFER_MS = 5 * 60 * 1000;
const BUFFER_RATIO = 0.2;
const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];

// Keeps an active session alive by refreshing the JWT shortly before it
// expires, but only when there has been user activity since the last
// refresh. An idle session is left to expire naturally, so the existing
// 401 handling in Api.js#checkResponse still logs the user out.
const useSessionKeepAlive = (enabled) => {
  const lastActivityRef = useRef(Date.now());
  const lastRefreshRef = useRef(Date.now());
  const lifetimeMsRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const markActivity = () => {
      lastActivityRef.current = Date.now();
    };

    ACTIVITY_EVENTS.forEach((event) =>
      document.addEventListener(event, markActivity, { passive: true }),
    );

    const refreshIfActive = async () => {
      const token = localStorage.getItem("authToken");
      const expiresAt = localStorage.getItem("authTokenExpiresAt");
      if (!token || !expiresAt) {
        return;
      }

      const expiresAtMs = new Date(expiresAt).getTime();
      if (Number.isNaN(expiresAtMs)) {
        return;
      }

      if (lifetimeMsRef.current === null) {
        lifetimeMsRef.current = Math.max(expiresAtMs - lastRefreshRef.current, 0);
      }

      const bufferMs = Math.min(
        Math.max(lifetimeMsRef.current * BUFFER_RATIO, MIN_BUFFER_MS),
        MAX_BUFFER_MS,
      );

      const now = Date.now();
      const nearingExpiry = now >= expiresAtMs - bufferMs;
      const activeSinceLastRefresh = lastActivityRef.current > lastRefreshRef.current;

      if (!nearingExpiry || !activeSinceLastRefresh) {
        return;
      }

      try {
        const refreshed = await api.refreshToken();
        localStorage.setItem("authToken", refreshed.token);
        localStorage.setItem("authTokenExpiresAt", refreshed.expiresAt);
        lastRefreshRef.current = Date.now();
        lifetimeMsRef.current = new Date(refreshed.expiresAt).getTime() - lastRefreshRef.current;
      } catch (error) {
        // A failed refresh (e.g. the token had already expired) falls
        // through to Api.js's normal 401/logout handling.
      }
    };

    const intervalId = setInterval(refreshIfActive, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        document.removeEventListener(event, markActivity),
      );
      clearInterval(intervalId);
    };
  }, [enabled]);
};

export default useSessionKeepAlive;
